import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangePicker, Stack, Text } from '@shared/ui';

const SAMPLE_WEEKS = ['2026-01-06', '2025-12-30', '2025-12-23', '2025-12-16', '2025-12-09'];

const QUICK_FILTERS = ['last-4-weeks', 'last-quarter', 'this-year'] as const;

export function DateRangePickerShowcase() {
  const { t, i18n } = useTranslation();
  const translate = (key: string) => t(key as never) as string;
  const [selectedStart, setSelectedStart] = useState<string | null>(SAMPLE_WEEKS[0] ?? null);
  const [selectedEnd, setSelectedEnd] = useState<string | null>(SAMPLE_WEEKS[0] ?? null);
  const [activeRange, setActiveRange] = useState<string | null>(null);

  const label = useMemo(() => {
    if (selectedStart && selectedEnd) {
      return `${selectedStart} – ${selectedEnd}`;
    }

    if (activeRange) {
      return translate(`analytics.weekly.filters.${activeRange}`);
    }

    return translate('analytics.weekly.filters.allTime');
  }, [activeRange, selectedEnd, selectedStart, translate]);

  return (
    <Stack gap="sm">
      <Text size="sm" className="font-semibold">
        {t('uiLibrary.forms.dateRangePicker.title')}
      </Text>
      <Text size="sm" color="muted">
        {t('uiLibrary.forms.dateRangePicker.description')}
      </Text>
      <DateRangePicker
        label={label}
        locale={i18n.language}
        minDate={SAMPLE_WEEKS.at(-1) ?? null}
        maxDate={SAMPLE_WEEKS[0] ?? null}
        selectedStart={selectedStart}
        selectedEnd={selectedEnd}
        quickFilters={QUICK_FILTERS.map((id) => ({
          id,
          label: t(`analytics.weekly.filters.${id}`),
        }))}
        activeQuickFilterId={activeRange}
        clearActive={!activeRange && !selectedStart && !selectedEnd}
        onRangeSelect={(start, end) => {
          setSelectedStart(start);
          setSelectedEnd(end);
          setActiveRange(null);
        }}
        onQuickFilterSelect={(filterId) => {
          setActiveRange(filterId);
          setSelectedStart(null);
          setSelectedEnd(null);
        }}
        onClear={() => {
          setActiveRange(null);
          setSelectedStart(null);
          setSelectedEnd(null);
        }}
        labels={{
          previous: t('analytics.weekly.filters.previous'),
          next: t('analytics.weekly.filters.next'),
          clear: t('analytics.weekly.filters.allTime'),
          quickFiltersTitle: t('analytics.weekly.filters.quickFilters'),
          monthPickerLabel: t('analytics.weekly.filters.label'),
        }}
      />
    </Stack>
  );
}
